import React from "react";
import { Container } from "react-bootstrap";
import Button from "react-bootstrap/Button";

import Particle from "../Particle";

const REGISTRATION_URL =
  "https://docs.google.com/forms/d/e/1FAIpQLSfyUZt4peUyGezMs72Taa9NH0s-IayV7VJpHF8kV7YQbZ1tpg/viewform";

const COURSES = [
  {
    id: "appropriate-technology",
    title: "Appropriate Technology Development Course",
    tagline: "Rethinking How We Create Technology",
    description: [
      "The Appropriate Technology Development course is a self-paced program for youth and adults (ages 14+) designed to shift participants from passive users of technology to thoughtful creators. Learners develop the skills to design and apply technology with purpose—grounded in ethics, community needs, and real-world impact. The course also supports current developers in strengthening their ability to provide meaningful, community-centered technical solutions.",
      "Technology is not neutral. From the industrial revolution to today’s digital age, innovation has expanded human capacity while reshaping society—and with that comes responsibility. This course challenges learners to move beyond adoption and into reflection, guiding them to create technologies that align with human values, promote justice, and contribute to the common good.",
    ],
    whatYouLearnIntro:
      "Learn how to design technology that is ethical, community-centered, and built for real-world impact.",
    whatYouLearn: [
      "Community-first problem framing: Read reality, listen deeply, and define needs with stakeholders.",
      "Ethical design thinking: Clarify who benefits, who bears risk, and how to reduce bias.",
      "Impact planning: Set goals, measures, and safeguards before you build.",
      "Sustainable solutions: Prioritize accessibility, maintainability, and local ownership.",
      "Communication: Present decisions clearly and document tradeoffs.",
      "Build & iterate: Use practical workflows to prototype, test, and improve.",
      "Lifelong practice: Strengthen reflection, collaboration, and responsible innovation.",
    ],
    priceLabel: "$99.99",
    payUrl: "https://checkout.square.site/merchant/MLAY6G9T625V1/checkout/VCRFVWRPBTXRFGMAMKK34RNV",
    paymentDetails: [
      "Course Length: 10 hours.",
      "Experience: No experience needed. A desire to understand how technology impacts society and to design solutions with purpose.",
      "Certificate of Completion awarded",
      "Course Setting: Remote.",
      "Software Requirements: No paid software required.",
      "Course Requirements: Access to a computer and internet. Openness to learning from diverse perspectives, engaging in ethical reflection, and considering the broader social and environmental impact of technology.",
    ],
  },
  {
    id: "justice-technology",
    title: "Justice and Technology Course",
    tagline: "Designing Technology with Justice at Its Core",
    description: [
      "The Justice and Technology Course helps youth and adults explore justice as both a human virtue and a guiding principle for how we design and use technology—practicing fairness, resisting prejudice, and avoiding extremes.",
      "Through interactive activities, case studies, and project-based learning, participants examine technology’s social impact and build solutions grounded in inclusivity and ethical innovation.",
      "Learners work with real-world examples like social media, AI decision tools, surveillance, privacy, and the digital divide to develop a practical “justice lens”—identify stakeholders, anticipate harms, and take action as creators, users, and advocates for the common good.",
    ],
    whatYouLearnIntro:
      "Build a “justice lens” for technology—think critically, act fairly, and design with responsibility.",
    whatYouLearn: [
      "Justice in tech: Define justice and spot how values—and bias—show up in tools and systems.",
      "Moderation & well-being: Develop healthier habits and boundaries for technology use.",
      "Ethical evaluation: Use frameworks to assess impact, risk, and real community needs.",
      "Community & unity: Understand how technology can strengthen (or weaken) connection and trust.",
      "Critical thinking online: Verify information, resist misinformation, and challenge prejudice.",
      "Design for justice: Create an app, campaign, initiative, policy, or prototype grounded in fairness and inclusion.",
    ],
    priceLabel: "$99.99",
    payUrl: "https://checkout.square.site/merchant/MLAY6G9T625V1/checkout/BJTDAYSW5WSLADJBUJL7UXET",
    paymentDetails: [
      "Course Length: 10 hours.",
      "Experience: No experience needed. A desire to explore ethical technology and justice in digital spaces.",
      "Certificate of Completion awarded",
      "Course Setting: Remote.",
      "Software Requirements: No paid software required.",
      "Course Requirements: Access to a computer and internet. Openness to diverse perspectives, ethical reasoning, and collaborative problem-solving.",
    ],
  },
  {
    id: "3d-modeling",
    title: "3D Modeling and Animation Training",
    tagline: "Bring Ideas to Life in 3D",
    description: [
      "The 3D Modeling & Animation Training at Choicest Fruit introduces learners to the creative and technical world of digital design. Perfect for individuals seeking new skills or enhancing their portfolio, this program equips participants to build 3D environments, characters, and animations while developing the ability to apply these skills in real-world industries—from gaming and advertising to architecture and engineering.",
      "Participants learn a practical end-to-end workflow—from concept and modeling to texturing, lighting, animation, and final renders—so projects look polished and production-ready.",
      "Along the way, learners complete guided projects and build a portfolio they can share for school, internships, freelance work, or creative opportunities.",
    ],
    whatYouLearnIntro:
      "Build the skills and confidence to bring ideas into 3D—whether you’re designing, animating, or engineering.",
    whatYouLearn: [
      "Blender Fundamentals: Navigate the interface and core tools.",
      "Modeling Skills: Build objects with clean geometry and topology.",
      "Texturing & Materials: UV map, create materials, and add surface detail.",
      "Lighting, Cameras & Rendering: Light scenes and export images/video/assets.",
      "Animation: Rig and animate characters, props, and environments.",
      "Sculpting & Effects: Add detail with sculpt tools, particles, smoke, and fluids.",
      "Polish & Pipeline: Composite edits and automate tasks with basic Python.",
    ],
    priceLabel: "$350",
    payUrl: "https://checkout.square.site/merchant/MLAY6G9T625V1/checkout/CPB4XIVAF5UW5YZ3LKVOTUCJ",
    paymentDetails: [
      "Training Length: 2 months.",
      "Experience: No programming experience needed.",
      "Certificate of Completion awarded",
      "Training Setting: In-Person | Remote | Hybrid.",
      "Requirements: Internet access; a scroll-wheel mouse is recommended for Blender.",
      "Practice: Plan for time outside sessions to build and refine projects.",
      "Portfolio: Complete projects you can showcase to schools, internships, or clients.",
    ],
  },
];

function ExploratoryLearning() {
  return (
    <Container fluid className="about-section exploratory-learning-page">
      <Particle />

      <Container className="exploratory-learning-container">
        <header className="exploratory-learning-hero">
          <h1 className="exploratory-learning-title">Exploratory Learning</h1>
          <p className="exploratory-learning-lead">
            Explore self-paced courses and training experiences that blend practical skill-building with ethical reflection and
            community-centered design.
          </p>
        </header>

        <div className="exploratory-learning-grid" aria-label="Exploratory learning courses">
          {COURSES.map((course) => (
            <section key={course.id} id={course.id} className="exploratory-learning-section">
              <div className="policy-card exploratory-learning-card">
                <div className="exploratory-learning-kicker">Course</div>
                <h2 className="exploratory-learning-card-title">{course.title}</h2>
                <p className="exploratory-learning-tagline">{course.tagline}</p>

                <div className="exploratory-learning-subcards" aria-label={`${course.title} details`}>
                  <div className="exploratory-learning-subcard">
                    <h3 className="exploratory-learning-subtitle">Overview</h3>
                    {course.description.map((paragraph) => (
                      <p key={paragraph} className="exploratory-learning-paragraph">
                        {paragraph}
                      </p>
                    ))}
                  </div>

                  <div className="exploratory-learning-subcard exploratory-learning-subcard--accent">
                    <h3 className="exploratory-learning-subtitle">Program Details</h3>
                    <ul className="exploratory-learning-list">
                      {course.paymentDetails.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>

                    <div className="exploratory-learning-subcard-actions" aria-label={`${course.title} actions`}>
                      <div className="exploratory-learning-price">{course.priceLabel}</div>
                      <div className="exploratory-learning-action-buttons">
                        <Button
                          as="a"
                          href={REGISTRATION_URL}
                          target="_blank"
                          rel="noreferrer"
                          className="custom-yellow-btn exploratory-learning-btn"
                        >
                          Register
                        </Button>
                        <Button
                          as="a"
                          href={course.payUrl}
                          target="_blank"
                          rel="noreferrer"
                          variant="outline-light"
                          className="exploratory-learning-btn exploratory-learning-btn--secondary"
                        >
                          Pay
                        </Button>
                      </div>
                      <p className="exploratory-learning-action-note">
                        Registration and payment are separate steps. If you’ve already registered, you can proceed directly to payment.
                      </p>
                    </div>
                  </div>

                  <div className="exploratory-learning-subcard">
                    <h3 className="exploratory-learning-subtitle">What You’ll Learn</h3>
                    <p className="exploratory-learning-paragraph">{course.whatYouLearnIntro}</p>
                    <ul className="exploratory-learning-list">
                      {course.whatYouLearn.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </section>
          ))}
        </div>
      </Container>
    </Container>
  );
}

export default ExploratoryLearning;
